import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { debounce } from 'lodash';
import type { Application } from '@/types/database.types';
import { AUTOSAVE_DELAY } from './save-constants';
import { prepareApplicationDataForSave } from './save-utils';

/**
 * Hook to handle automatic saving of application data
 * Note: Auto-saving is now disabled by default and only used via manual triggers
 */
export const useAutosave = (
  application: Application | null,
  setApplication: (app: Application | null) => void
) => {
  const { toast } = useToast();
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // This save function is now only called explicitly, not on every change
  const saveApplicationData = useCallback(async (data: Partial<Application>) => {
    try {
      if (!application?.id || isSaving) return;
      
      setIsSaving(true);
      console.log("Saving application data:", data);

      // Prepare data for database update using our utility
      const updateData = prepareApplicationDataForSave(application, data);

      console.log("Sending update to database:", updateData);
      
      const { error } = await supabase
        .from('applications')
        .update(updateData)
        .eq('id', application.id);

      if (error) throw error;

      // Update the local application state, preserving any fields not in the update
      setApplication({
        ...application,
        ...data
      } as Application);
      
      setLastSaved(new Date());
      console.log("Save successful");
      
      return true;
    } catch (error: any) {
      console.error('Save error:', error);
      toast({
        title: 'Save failed',
        description: 'Your changes could not be saved. Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [application, toast, setApplication, isSaving]);

  // We keep the debounced version for compatibility, but it's now only used explicitly
  const debouncedSave = useCallback(
    debounce((data: Partial<Application>) => {
      saveApplicationData(data);
    }, AUTOSAVE_DELAY),
    [saveApplicationData]
  );

  return {
    lastSaved,
    debouncedSave,
    saveApplicationData,
    isSaving
  };
};
